var _excluded = ["appRootSelector", "children", "className", "footer", "includeCssBaseline", "initialState", "leftDrawer", "leftDrawerOpensAt", "onDrawerStateChange", "appBar"];

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import * as React from 'react';
import clsx from 'clsx';
import { isEqual } from 'lodash';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import { useMediaQuery, styled, GlobalStyles, useTheme } from '@mui/material';
import { Drawer, States } from './Drawer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var Root = styled('div', {
  name: 'MnrlAppShell',
  slot: 'Root',
  overridesResolver: function overridesResolver(props, styles) {
    return [styles.root];
  }
})({
  display: 'flex',
  flex: '1 0 auto',
  position: 'relative'
});
var Content = styled('div', {
  name: 'MnrlAppShell',
  slot: 'Content',
  overridesResolver: function overridesResolver(props, styles) {
    return [styles.content];
  }
})({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column'
});
var StyledScopedCssBaseline = styled(ScopedCssBaseline, {
  name: 'MnrlAppShell',
  slot: 'Baseline',
  overridesResolver: function overridesResolver(props, styles) {
    return [styles.baseline];
  }
})(function (_ref) {
  var hasAppRootSelector = _ref.ownerState.hasAppRootSelector;
  return hasAppRootSelector ? {
    display: 'flex',
    flex: '1 0 auto',
    flexDirection: 'column'
  } : undefined;
});
var Toolbar = styled('div', {
  name: 'MnrlAppShell',
  slot: 'Toolbar',
  overridesResolver: function overridesResolver(props, styles) {
    return [styles.toolbar];
  }
})(function (_ref2) {
  var theme = _ref2.theme;
  return theme.mixins.toolbar;
}); // eslint-disable-next-line @typescript-eslint/no-empty-function

var noOp = function noOp() {};

var defaultInitialState = {
  leftDrawer: {
    state: States.Closed,
    userToggled: false
  }
};
var LeftDrawerContext = /*#__PURE__*/React.createContext([defaultInitialState.leftDrawer, noOp]);
export var useLeftDrawerState = function useLeftDrawerState() {
  return React.useContext(LeftDrawerContext);
};

var useWide = function useWide(_ref3) {
  var minWidth = _ref3.minWidth,
      setState = _ref3.setState,
      userToggled = _ref3.userToggled;
  var wide = useMediaQuery(minWidth ? "(min-width: " + minWidth + ")" : '');
  React.useEffect(function () {
    if (minWidth && !userToggled) {
      setState({
        state: wide ? States.Open : States.Closed
      });
    }
  }, [minWidth, setState, userToggled, wide]);
};

var useDrawerState = function useDrawerState(_ref4) {
  var initialState = _ref4.initialState,
      onDrawerStateChange = _ref4.onDrawerStateChange,
      _ref4$side = _ref4.side,
      side = _ref4$side === void 0 ? 'left' : _ref4$side;

  var _React$useState = React.useState(initialState),
      drawerState = _React$useState[0],
      setDrawerState = _React$useState[1];

  var setDrawerPartialState = React.useCallback(function (value) {
    setDrawerState(function (prevState) {
      var val = typeof value === 'function' ? value(prevState) : value;

      var nextState = _extends({}, prevState, val);

      if (onDrawerStateChange && !isEqual(prevState, nextState)) {
        setTimeout(function () {
          return onDrawerStateChange(side, prevState, nextState);
        }, 0);
      }

      return nextState;
    });
  }, [onDrawerStateChange, setDrawerState, side]);
  return [drawerState, setDrawerPartialState];
};

export var AppShell = function AppShell(_ref5) {
  var _ref6;

  var _ref5$appRootSelector = _ref5.appRootSelector,
      appRootSelector = _ref5$appRootSelector === void 0 ? '#root' : _ref5$appRootSelector,
      children = _ref5.children,
      className = _ref5.className,
      footer = _ref5.footer,
      _ref5$includeCssBasel = _ref5.includeCssBaseline,
      includeCssBaseline = _ref5$includeCssBasel === void 0 ? true : _ref5$includeCssBasel,
      _ref5$initialState = _ref5.initialState,
      initialState = _ref5$initialState === void 0 ? defaultInitialState : _ref5$initialState,
      leftDrawer = _ref5.leftDrawer,
      _ref5$leftDrawerOpens = _ref5.leftDrawerOpensAt,
      leftDrawerOpensAt = _ref5$leftDrawerOpens === void 0 ? '1300px' : _ref5$leftDrawerOpens,
      onDrawerStateChange = _ref5.onDrawerStateChange,
      appBar = _ref5.appBar,
      restProps = _objectWithoutPropertiesLoose(_ref5, _excluded);

  var theme = useTheme();

  var _useDrawerState = useDrawerState({
    initialState: initialState.leftDrawer || defaultInitialState.leftDrawer,
    onDrawerStateChange: onDrawerStateChange
  }),
      leftDrawerState = _useDrawerState[0],
      setLeftDrawerState = _useDrawerState[1];

  useWide({
    minWidth: leftDrawerOpensAt,
    setState: setLeftDrawerState,
    userToggled: leftDrawerState.userToggled
  });

  var content = /*#__PURE__*/_jsx(LeftDrawerContext.Provider, {
    value: [leftDrawerState, setLeftDrawerState],
    children: /*#__PURE__*/_jsxs(Root, _extends({
      className: clsx('MnrlAppShell-root', className)
    }, restProps, {
      children: [appBar, leftDrawer && /*#__PURE__*/_jsx(Drawer, {
        PaperProps: {
          elevation: 1
        },
        setState: setLeftDrawerState,
        state: leftDrawerState,
        children: leftDrawer
      }), /*#__PURE__*/_jsxs(Content, {
        className: "MnrlAppShell-content",
        children: [appBar && /*#__PURE__*/_jsx(Toolbar, {
          className: "MnrlAppShell-toolbar"
        }), children, footer]
      })]
    }))
  });

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GlobalStyles, {
      styles: {
        html: _extends({}, appRootSelector && (_ref6 = {
          '&, body': {
            height: '100%'
          }
        }, _ref6["& body, & " + appRootSelector] = {
          display: 'flex',
          flexDirection: 'column'
        }, _ref6["& " + appRootSelector] = {
          flex: '1 0 auto'
        }, _ref6), includeCssBaseline && {
          '&': {
            colorScheme: theme.palette.mode,
            backgroundColor: theme.palette.background["default"]
          },
          '& body': {
            margin: 0
          }
        })
      }
    }), includeCssBaseline ? /*#__PURE__*/_jsx(StyledScopedCssBaseline, {
      className: "MnrlAppShell-baseline",
      ownerState: {
        hasAppRootSelector: Boolean(appRootSelector)
      },
      children: content
    }) : content]
  });
};